import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const ChatbotMgrContext = createContext({
  open: false,
  toggleOpen: () => {},
  setOpen: () => {},
  registerUiComponent: () => () => {},
  uiComponents: () => [],
});

/**
 * Shares the chatbot panel's open state between the Help button and the panel, and keeps the
 * registry of mounted UI components that have described themselves to the chatbot (see useChatbotRegistration).
 */
export const ChatbotMgrProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => setOpen((prev) => !prev), []);

  // A ref rather than state: components re-register on every render and nothing needs to re-render
  // when they do; the panel reads the registry at send time
  const nameToUiComponentRef = useRef({});

  const registerUiComponent = useCallback((name, uiComponent) => {
    nameToUiComponentRef.current[name] = uiComponent;
    return () => {
      if (nameToUiComponentRef.current[name] === uiComponent) delete nameToUiComponentRef.current[name];
    };
  }, []);

  const uiComponents = useCallback(
    () => Object.entries(nameToUiComponentRef.current).map(([name, uiComponent]) => ({ name, ...uiComponent })),
    [],
  );

  const value = useMemo(
    () => ({ open, toggleOpen, setOpen, registerUiComponent, uiComponents }),
    [open, toggleOpen, registerUiComponent, uiComponents],
  );

  return <ChatbotMgrContext.Provider value={value}>{children}</ChatbotMgrContext.Provider>;
};

export const useChatbotMgr = () => useContext(ChatbotMgrContext);

/**
 * Lets a UI component describe itself to the chatbot while it is mounted.
 *
 * name: what the user would call the component, e.g. 'Validate button' or 'Direct Sales list'. It is
 * both the registry key and how the chatbot refers to the component, so it must be unique on the page.
 * Register only page-level components, never anything rendered per row or per data point.
 * description: what the component is and its current state, in business terms. Pass a falsy value
 * to stay unregistered, e.g. when the component will not render; hooks must still be called.
 * dataFetchParams (optional): the exact parameters the component fetched its data with, so the
 * chatbot can fetch the same thing: { data: { module, plugin, reqParam } } or
 * { rows: { module, entity, expr, sortModel } }.
 */
export const useChatbotRegistration = (name, description, dataFetchParams) => {
  const { registerUiComponent } = useChatbotMgr();
  const dataFetchParamsJson = dataFetchParams ? JSON.stringify(dataFetchParams) : undefined;
  useEffect(() => {
    if (!name || !description) return undefined;
    return registerUiComponent(name, {
      description,
      dataFetchParams: dataFetchParamsJson ? JSON.parse(dataFetchParamsJson) : undefined,
    });
  }, [registerUiComponent, name, description, dataFetchParamsJson]);
};
